import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import { PARTNER_IDS_WITH_YARD_SIGN_BOTTOM_RIDER } from 'common/jobConstants';

import { SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR } from 'api/actionTypes';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';

import { PARTNER_ID_3Z_PROPERTIES, PARTNER_ID_STONE_OAK } from 'common/partnerConstants';

import {
  INVENTORY_ITEM_TYPE_YARDSIGN_FRAME,
  INVENTORY_ITEM_TYPE_YARDSIGN_PANEL,
  INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_BOTTOM,
  INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP,
  INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_3Z,
  INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_STONE_OAK,
} from 'common/inventoryConstants';

import ButtonDeprecated, { BUTTON_THEME_BLUE_GREYCLIFF } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrFormikErrorMessage from 'client/components/SrFormikErrorMessage';
import SrBarcodeScanner from 'client/components/SrBarcodeScanner';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import './SrSunSetupDriverJobPage.less';

const YARD_SIGN_STATE_INITIAL = 'initial';
const YARD_SIGN_STATE_URL_SET = 'urlSet';

const getFormFields = ({
  values = {},
  shouldAskForBottomRider,
  setIsBarcodeScannerOpen,
  setFocusedField,
}) => {
  return [
    {
      id: 'yardSignUrl',
      title: 'Yard Sign URL',
      type: 'short_text',
      properties: {
        placeholder: 'Write URL code here...',
      },
      validations: ['required'],
    },
    {
      id: 'yardSignPanelSerialNumber',
      title: 'Panel Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('yardSignPanelSerialNumber');
        },
      },
    },
    {
      id: 'yardSignTopRiderSerialNumber',
      title: 'Sunroom Top Rider Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('yardSignTopRiderSerialNumber');
        },
      },
    },
    {
      id: 'yardSignFrameSerialNumber',
      title: 'Frame Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('yardSignFrameSerialNumber');
        },
      },
    },
    showFieldIf(shouldAskForBottomRider && !values.yardSignBottomRiderIsNotInPossession, {
      id: 'yardSignBottomRiderSerialNumber',
      title: 'Bottom Rider Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('yardSignBottomRiderSerialNumber');
        },
      },
    }),
    showFieldIf(shouldAskForBottomRider, {
      id: 'yardSignBottomRiderIsNotInPossession',
      title: values.yardSignBottomRiderIsNotInPossession ? 'Bottom Rider Barcode Number' : null,
      type: 'simple_checkbox',
      properties: {
        children: 'I do not have a Bottom Rider to install.',
      },
    }),
  ];
};

const SrSunSetupDriverJobYardSignSetup = ({
  partnerId,
  formikBag,
  itemScans,
  yardSignUrl,
  getCurrentLocation,
  requestGeolocationPermission,
  handleYardSignRegistration,
}) => {
  const [viewState, setViewState] = useState(
    yardSignUrl ? YARD_SIGN_STATE_URL_SET : YARD_SIGN_STATE_INITIAL,
  );
  const [yardSignMissingError, setYardSignMissingError] = useState(false);
  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const dispatch = useDispatch();

  const _onYardSignUrl = (yardSignUrl) => {
    setYardSignMissingError(!yardSignUrl);
    setViewState(yardSignUrl ? YARD_SIGN_STATE_URL_SET : YARD_SIGN_STATE_INITIAL);
  };

  const _createGeolocationError = ({ errorMessage }) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR,
      payload: {
        errorMessage,
      },
    });
  };

  const _requestGeolocationPermission = async () => {
    setIsLocationLoading(true);
    try {
      await requestGeolocationPermission();
      const location = await getCurrentLocation();

      setIsLocationLoading(false);
      return location;
    } catch (error) {
      _createGeolocationError({
        errorMessage: error.message,
      });
      setIsLocationLoading(false);
      throw error;
    }
  };

  const getBottomRiderType = () => {
    switch (partnerId) {
      case PARTNER_ID_3Z_PROPERTIES:
        return INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_3Z;
      case PARTNER_ID_STONE_OAK:
        return INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_STONE_OAK;
      default:
        return INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_BOTTOM;
    }
  };

  const _onSubmit = async (formValues) => {
    const location = await _requestGeolocationPermission();
    const { latitude, longitude } = location;
    const {
      yardSignUrl,
      yardSignPanelSerialNumber,
      yardSignFrameSerialNumber,
      yardSignTopRiderSerialNumber,
      yardSignBottomRiderSerialNumber,
    } = formValues;

    const itemGroup = lodashCompact([
      yardSignPanelSerialNumber && {
        type: INVENTORY_ITEM_TYPE_YARDSIGN_PANEL,
        serialNumber: yardSignPanelSerialNumber,
      },
      yardSignFrameSerialNumber && {
        type: INVENTORY_ITEM_TYPE_YARDSIGN_FRAME,
        serialNumber: yardSignFrameSerialNumber,
      },
      yardSignTopRiderSerialNumber && {
        type: INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP,
        serialNumber: yardSignTopRiderSerialNumber,
      },
      yardSignBottomRiderSerialNumber && {
        type: getBottomRiderType(),
        serialNumber: yardSignBottomRiderSerialNumber,
      },
    ]);

    handleYardSignRegistration({
      itemGroup,
      yardSignUrl,
      latitude,
      longitude,
    }).then((yardSignUrl) => {
      return _onYardSignUrl(yardSignUrl);
    });
  };

  const _onFieldScan = ({ barcode, formikBag }) => {
    if (!focusedField) {
      return;
    }
    formikBag.setFieldValue(focusedField, barcode);
    setFocusedField(null);
    setIsBarcodeScannerOpen(false);
  };

  const _handleScannerClose = () => {
    setIsBarcodeScannerOpen(false);
    setFocusedField(null);
  };

  const getItemScansSerialNumberMapping = () => {
    const itemScansSerialNumberMapping = {};
    itemScans.forEach((itemScan) => {
      itemScansSerialNumberMapping[itemScan.inventoryItemTypeId] = itemScan.serialNumber;
    });
    return itemScansSerialNumberMapping;
  };

  const initialFormValues = {
    yardSignUrl: yardSignUrl || '',
    yardSignBottomRiderIsNotInPossession: false,
  };

  const itemScansSerialNumberMapping = getItemScansSerialNumberMapping();
  const shouldAskForBottomRider = PARTNER_IDS_WITH_YARD_SIGN_BOTTOM_RIDER.includes(partnerId);

  return (
    <div styleName="card" className="fieldsContainer" id="field-yardSignUrl">
      <span styleName="cardTitle">Register Yard Sign</span>

      <SrBox margin="16px 0 0">
        {yardSignMissingError && (
          <SrBox padding="0 0 16px">
            <SrErrorMessage>Yard sign not found. Please try again.</SrErrorMessage>
          </SrBox>
        )}

        {viewState === YARD_SIGN_STATE_URL_SET && (
          <>
            <SrBox styleName="jobDetailsBox" padding="16px 0 16px">
              <SrBox marginBottom="16px">
                <SrBox styleName="title">Yard Sign URL</SrBox>
                <SrBox styleName="serialNumber">{yardSignUrl}</SrBox>
              </SrBox>
              <SrBox borderTop="1px solid #E0E0E0" paddingTop="8px" marginBottom="16px">
                <SrBox styleName="title">Panel Barcode Number</SrBox>
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_PANEL] || '--'}
                </SrBox>
              </SrBox>
              <SrBox borderTop="1px solid #E0E0E0" paddingTop="8px" marginBottom="16px">
                <SrBox styleName="title">Sunroom Top Rider Barcode Number</SrBox>
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP] || '--'}
                </SrBox>
              </SrBox>
              <SrBox borderTop="1px solid #E0E0E0" paddingTop="8px">
                <SrBox styleName="title">Frame Barcode Number</SrBox>
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_FRAME] || '--'}
                </SrBox>
              </SrBox>
              {shouldAskForBottomRider && (
                <SrBox borderTop="1px solid #E0E0E0" paddingTop="8px" marginTop="16px">
                  <SrBox styleName="title">Bottom Rider Barcode Number</SrBox>
                  <SrBox styleName="serialNumber">
                    {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_BOTTOM] ||
                      '--'}
                  </SrBox>
                </SrBox>
              )}
            </SrBox>
            <SrBox margin="16px 0 0">
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE_GREYCLIFF}
                onClick={() => setViewState(YARD_SIGN_STATE_INITIAL)}
              >
                Scan Again
              </ButtonDeprecated>
            </SrBox>
          </>
        )}

        {viewState === YARD_SIGN_STATE_INITIAL && (
          <Formik
            onSubmit={_onSubmit}
            initialValues={createInitialValues({
              formFields: getFormFields({
                shouldAskForBottomRider,
                values: getFormFields({ formValues: initialFormValues }),
                setIsBarcodeScannerOpen,
                setFocusedField,
              }),
              defaultValues: initialFormValues,
            })}
            validate={(formValues) =>
              createFormValidationFn(
                getFormFields({
                  shouldAskForBottomRider,
                  values: formValues,
                  setIsBarcodeScannerOpen,
                  setFocusedField,
                }),
              )
            }
            render={(formikBag) => {
              const { values } = formikBag;
              const yardSignUrlFormFields = getFormFields({
                values,
                shouldAskForBottomRider,
                setIsBarcodeScannerOpen,
                setFocusedField,
              });
              return (
                <Form>
                  {yardSignUrlFormFields.map((formField) => (
                    <SrFormikField
                      formikBag={formikBag}
                      formField={formField}
                      theme={SR_FIELD_THEME_ADOBE}
                      key={formField.id}
                    />
                  ))}
                  {isBarcodeScannerOpen && (
                    <SrBarcodeScanner
                      onText={(barcode) => _onFieldScan({ barcode, formikBag })}
                      onClose={_handleScannerClose}
                    />
                  )}
                  <ButtonDeprecated
                    margin="24px 0 0"
                    type="submit"
                    theme={BUTTON_THEME_BLUE_GREYCLIFF}
                    disabled={isLocationLoading}
                  >
                    {isLocationLoading ? 'Loading...' : 'Submit'}
                  </ButtonDeprecated>
                </Form>
              );
            }}
          />
        )}
      </SrBox>
      <SrFormikErrorMessage id="yardSignUrl" formikBag={formikBag} />
    </div>
  );
};

SrSunSetupDriverJobYardSignSetup.propTypes = {
  partnerId: PropTypes.string.isRequired,
  formikBag: PropTypes.object.isRequired,
  itemScans: PropTypes.array.isRequired,
  yardSignUrl: PropTypes.string,
  getCurrentLocation: PropTypes.func.isRequired,
  requestGeolocationPermission: PropTypes.func.isRequired,
  handleYardSignRegistration: PropTypes.func.isRequired,
};

SrSunSetupDriverJobYardSignSetup.displayName = 'SrSunSetupDriverJobYardSignSetup';

export default SrSunSetupDriverJobYardSignSetup;
